import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Crown, Flame, Sparkles, Trophy } from "lucide-react";

const ACCENT = ["#ffd84a", "#c0c0c0", "#cd7f32"]; // gold / silver / bronze

function fmtCountdown(ms) {
  if (ms <= 0) return "Resetuje se uskoro…";
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  return `${h}h ${m}m`;
}

export default function WeeklyPromoters() {
  const [data, setData] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const { data } = await api.get("/leaderboard/promoters/weekly?limit=3");
        if (mounted) setData(data);
      } catch {}
    };
    load();
    const i = setInterval(load, 60_000); // refresh every minute
    const c = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      mounted = false;
      clearInterval(i);
      clearInterval(c);
    };
  }, []);

  if (!data) return null;
  const resetsIn = data.resets_at ? new Date(data.resets_at).getTime() - now : 0;
  const promoters = data.promoters || [];

  return (
    <section data-testid="weekly-promoters" className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Crown className="w-5 h-5 text-[#ffd84a]" />
          <h2 className="font-display text-2xl font-bold">
            Najbolji <span className="text-[#ffd84a]">promoteri ove nedelje</span>
          </h2>
        </div>
        <div className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 font-mono">
          Nedelja #{data.week_number} · resetuje se za{" "}
          <span className="text-[#ff7a18]" data-testid="weekly-resets-in">{fmtCountdown(resetsIn)}</span>
        </div>
      </div>

      {promoters.length === 0 ? (
        <div className="nasa-card p-8 text-center text-sm text-zinc-500" data-testid="weekly-promoters-empty">
          <Sparkles className="w-5 h-5 text-[#22ff77] inline mr-2" />
          Još nema aktivacija ove nedelje — budite prvi koji će pozvati prijatelja na aktivaciju!
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {promoters.map((p, i) => (
            <div
              key={p.user_id}
              data-testid={`weekly-promoter-${p.rank}`}
              className="relative nasa-card p-5 overflow-hidden"
              style={{
                borderColor: i === 0 ? `${ACCENT[i]}80` : "rgba(255,255,255,0.08)",
                boxShadow: i === 0 ? `0 0 0 1px ${ACCENT[i]}66, 0 0 30px ${ACCENT[i]}20` : undefined,
              }}
            >
              {p.is_promoter_of_week && (
                <div
                  data-testid="promoter-of-week-badge"
                  className="absolute top-3 right-3 flex items-center gap-1 text-[10px] uppercase tracking-[0.25em] px-2 py-1 rounded-sm"
                  style={{ background: "#ffd84a", color: "#000" }}
                >
                  <Flame className="w-3 h-3" /> Promoter nedjelje
                </div>
              )}
              <div className="flex items-center gap-3">
                <div
                  className="w-14 h-14 rounded-sm overflow-hidden border flex items-center justify-center font-display text-xl font-bold shrink-0"
                  style={{ borderColor: `${ACCENT[i]}80`, background: "rgba(255,255,255,0.04)", color: ACCENT[i] }}
                >
                  {p.picture ? (
                    <img src={p.picture} alt={p.name} className="w-full h-full object-cover" />
                  ) : (
                    `#${p.rank}`
                  )}
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                    {i === 0 ? "1. mesto" : i === 1 ? "2. mesto" : "3. mesto"}
                  </div>
                  <div className="font-display text-lg font-bold truncate" title={p.name}>
                    {p.name}
                  </div>
                  {p.country && <div className="text-[10px] text-zinc-500 truncate">{p.country}</div>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-5">
                <div className="border border-white/10 bg-black/40 p-2.5 rounded-sm">
                  <div className="text-[9px] uppercase tracking-widest text-zinc-500">Aktivacije</div>
                  <div className="font-display text-xl font-bold text-[#22ff77]">{p.weekly_activations}</div>
                </div>
                <div className="border border-white/10 bg-black/40 p-2.5 rounded-sm">
                  <div className="text-[9px] uppercase tracking-widest text-zinc-500">Tokeni</div>
                  <div className="font-display text-xl font-bold text-[#ff7a18]">+{p.weekly_tokens}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="text-[11px] text-zinc-500 leading-relaxed">
        <Trophy className="inline w-3 h-3 text-[#ffd84a] mr-1" />
        Lista se automatski ažurira i resetuje svake nedelje (svakog ponedeljka 00:00 UTC). Svaka nedelja je nova
        prilika da postanete <span className="text-[#ffd84a]">Promoter nedjelje</span>.
      </div>
    </section>
  );
}
